// Shared types + helpers for the unified Insights system.
// All five content types (news, event, webinar, blog, evidence) live in the
// `insights` table — the `kind` column drives which template and CTA are used.

export type ContentKind = "news" | "event" | "webinar" | "blog" | "evidence" | "whitepaper" | "other";


export const CONTENT_KIND_LABEL: Record<ContentKind, string> = {
  news: "News",
  event: "Events",
  webinar: "Media",
  blog: "Blog",
  evidence: "Evidence",
  whitepaper: "White paper",
  other: "Literature",
};

export const CONTENT_KIND_SINGULAR: Record<ContentKind, string> = {
  news: "News",
  event: "Event",
  webinar: "Media",
  blog: "Article",
  evidence: "Evidence",
  whitepaper: "White paper",
  other: "Literature",
};


/** Sub-categories displayed under the Media kind. */
export const MEDIA_SUBCATEGORIES = [
  "Webinars",
  "Product updates",
  "Testimonials",
  "Podcasts",
] as const;
export type MediaSubcategory = (typeof MEDIA_SUBCATEGORIES)[number];

const MEDIA_SUB_SET = new Set<string>(MEDIA_SUBCATEGORIES);

/** Return up to 2 Media subcategory labels for a given item (primary + secondary). */
export function getMediaCategories(
  item: Pick<ContentItem, "category" | "tags">,
): MediaSubcategory[] {
  const out: MediaSubcategory[] = [];
  const push = (v: string | null | undefined) => {
    if (!v) return;
    const trimmed = v.trim();
    if (MEDIA_SUB_SET.has(trimmed) && !out.includes(trimmed as MediaSubcategory)) {
      out.push(trimmed as MediaSubcategory);
    }
  };
  push(item.category);
  for (const t of item.tags ?? []) {
    if (out.length >= 2) break;
    push(t);
  }
  return out;
}

/** Derive whether a Media item is upcoming live, on-demand, or neither. */
export function getMediaState(
  item: Pick<ContentItem, "event_starts_at" | "recording_url" | "video_id">,
): "upcoming" | "on-demand" | null {
  const hasRecording = Boolean(item.recording_url || item.video_id);
  if (hasRecording) return "on-demand";
  if (item.event_starts_at && new Date(item.event_starts_at).getTime() > Date.now()) {
    return "upcoming";
  }
  if (item.event_starts_at) return "on-demand";
  return null;
}

export const CONTENT_KIND_SLUG: Record<ContentKind, string> = {
  news: "news",
  event: "events",
  webinar: "webinars",
  blog: "blog",
  evidence: "evidence",
  whitepaper: "whitepaper",
  other: "other",
};

// Reverse lookup: URL segment → kind.
export const SLUG_TO_KIND: Record<string, ContentKind> = {
  news: "news",
  events: "event",
  event: "event",
  webinars: "webinar",
  webinar: "webinar",
  blog: "blog",
  blogs: "blog",
  evidence: "evidence",
  whitepaper: "whitepaper",
  other: "other",
};

export const KIND_FILTERS: { kind: ContentKind | "all"; label: string }[] = [
  { kind: "all", label: "All" },
  { kind: "news", label: "News" },
  { kind: "webinar", label: "Media" },
  { kind: "blog", label: "Blog" },
  { kind: "evidence", label: "Evidence" },
  { kind: "other", label: "Literature" },
];

export interface Speaker {
  name: string;
  title?: string;
  org?: string;
  photo_url?: string;
  bio?: string;
  linkedin_url?: string;
}

export interface AgendaItem {
  time?: string;
  title: string;
  detail?: string;
}

export interface InlinePromo {
  title: string;
  text: string;
  cta_label: string;
  cta_url: string;
  image_url: string;
  source_insight_id?: string | null;
  category?: string | null;
}

export interface ContentItem {
  id: string;
  slug: string;
  kind: ContentKind;
  title: string;
  summary: string | null;
  subtitle: string | null;
  body_md: string | null;
  footer_md: string | null;
  category: string | null;
  cover_image_url: string | null;
  cover_image_alt: string | null;
  status: "draft" | "published" | "archived";
  published_at: string | null;
  tags: string[];
  featured: boolean;
  sort_order: number;
  cta_label: string | null;
  cta_url: string | null;
  related_ids: string[];
  seo_title: string | null;
  seo_description: string | null;
  // event/webinar
  event_starts_at: string | null;
  event_ends_at: string | null;
  event_timezone: string | null;
  location: string | null;
  is_virtual: boolean;
  registration_url: string | null;
  show_inline_form: boolean;
  recording_url: string | null;
  video_provider: string | null;
  video_id: string | null;
  agenda: AgendaItem[];
  speakers: Speaker[];
  // evidence
  gated: boolean;
  download_url: string | null;
  gated_form_source: string | null;
  hubspot_form_id: string | null;
  evidence_type: string | null;
  authors: string | null;
  journal: string | null;
  year: number | null;
  external_url: string | null;
  abstract: string | null;

  // references / sources (publicly available citations for the article)
  sources: { title: string; url: string }[];
  // byline + import metadata (used by blog posts carried over from the legacy site)
  author_name: string | null;
  author_avatar_url: string | null;
  read_time_minutes: number | null;
  toc_enabled?: boolean;
  toc_levels?: number[];
  legacy_source_url: string | null;
  // In-article cross-promo blocks (rendered at ~25% and ~75% of blog body).
  inline_promos: (InlinePromo | null)[];
  // Structured document model (used by the whitepaper template's paginator).
  blocks: unknown;
  // Whitepapers only: when false, hidden from public listings + noindex.
  // When true, appears like a normal insight (listed + indexable).
  listed: boolean;
}

const FIELDS =
  "id, slug, kind, title, summary, subtitle, body_md, footer_md, category, cover_image_url, cover_image_alt, status, published_at, tags, featured, sort_order, cta_label, cta_url, related_ids, seo_title, seo_description, event_starts_at, event_ends_at, event_timezone, location, is_virtual, registration_url, show_inline_form, recording_url, video_provider, video_id, agenda, speakers, gated, download_url, gated_form_source, hubspot_form_id, evidence_type, authors, journal, year, external_url, abstract, sources, author_name, author_avatar_url, read_time_minutes, legacy_source_url, inline_promos, blocks, listed, toc_enabled, toc_levels";



export const CONTENT_FIELDS = FIELDS;

export function detailHref(item: Pick<ContentItem, "kind" | "slug">) {
  return item.kind === "event" ? `/event/${item.slug}` : `/insight/${item.slug}`;
}

export function defaultCtaLabel(kind: ContentKind, item?: Partial<ContentItem>): string {
  if (item?.cta_label) return item.cta_label;
  switch (kind) {
    case "event":
      return "View event";
    case "webinar":
      return item?.recording_url || item?.video_id ? "Watch" : "Register";
    case "evidence":
      return item?.gated ? "Get the report" : "Download";
    case "news":
      return "Read the announcement";
    case "whitepaper":
      return "Read the white paper";
    default:
      return "Read more";
  }
}

export function formatDateLabel(item: ContentItem): string {
  if (item.event_starts_at) {
    const d = new Date(item.event_starts_at);
    return d.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }
  if (item.published_at) {
    const d = new Date(item.published_at);
    return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
  }
  return "";
}

export function isUpcoming(item: ContentItem): boolean {
  if (!item.event_starts_at) return false;
  return new Date(item.event_starts_at).getTime() > Date.now();
}

/**
 * Map common timezone abbreviations admins type ("CET", "CEST", "EST", …)
 * to IANA zone names that Intl can format with. Without this, abbreviations
 * silently fall back to viewer-local time, producing a wrong clock + label
 * mismatch (e.g. London visitor sees "14:30 CEST" instead of "15:30 CEST").
 */
const TZ_ABBREV_TO_IANA: Record<string, string> = {
  CET: "Europe/Berlin",
  CEST: "Europe/Berlin",
  BST: "Europe/London",
  GMT: "Europe/London",
  WET: "Europe/Lisbon",
  WEST: "Europe/Lisbon",
  EET: "Europe/Helsinki",
  EEST: "Europe/Helsinki",
  EST: "America/New_York",
  EDT: "America/New_York",
  CT: "America/Chicago",
  CST: "America/Chicago",
  CDT: "America/Chicago",
  MST: "America/Denver",
  MDT: "America/Denver",
  PST: "America/Los_Angeles",
  PDT: "America/Los_Angeles",
  UTC: "UTC",
  Z: "UTC",
};

/**
 * Resolve a stored timezone string to an IANA name Intl can format with.
 * Returns the IANA name if valid (either already IANA or a known
 * abbreviation), else null.
 */
/**
 * Resolve a stored timezone string to an IANA name Intl can format with.
 * Returns the IANA name if valid (either already IANA or a known
 * abbreviation), else null.
 */
export function resolveIanaZone(tz: string | null | undefined): string | null {
  if (!tz) return null;
  const trimmed = tz.trim();
  if (!trimmed) return null;
  const mapped = TZ_ABBREV_TO_IANA[trimmed.toUpperCase()] ?? trimmed;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: mapped });
    return mapped;
  } catch {
    return null;
  }
}



/** Format an ISO timestamp as HH:MM in the event's configured timezone. */
export function formatEventTime(
  iso: string,
  tz: string | null | undefined,
): string {
  const opts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  const iana = resolveIanaZone(tz);
  if (iana) opts.timeZone = iana;
  return new Date(iso).toLocaleTimeString(undefined, opts);
}

/**
 * Returns a trailing " (TZ)" label for the configured event timezone.
 * Uses the short name (e.g. CET/CEST) derived from the IANA zone so it
 * stays correct across DST; falls back to the stored string verbatim.
 */
export function formatTzLabel(
  iso: string | null | undefined,
  tz: string | null | undefined,
): string {
  if (!tz) return "";
  const iana = resolveIanaZone(tz);
  if (iso && iana) {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: iana,
        timeZoneName: "short",
      }).formatToParts(new Date(iso));
      const name = parts.find((p) => p.type === "timeZoneName")?.value;
      if (name) return ` (${name})`;
    } catch {
      // fall through
    }
  }
  return ` (${tz})`;
}

// Coerce raw JSON shapes from Supabase to typed.
type Raw = Record<string, unknown>;
export function toContentItem(row: Raw): ContentItem {
  const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
  return {
    id: String(row.id),
    slug: String(row.slug ?? ""),
    kind: ((row.kind as ContentKind) ?? "blog") as ContentKind,
    title: String(row.title ?? ""),
    summary: (row.summary as string) ?? null,
    subtitle: (row.subtitle as string) ?? null,
    body_md: (row.body_md as string) ?? null,
    footer_md: (row.footer_md as string) ?? null,
    category: (row.category as string) ?? null,
    cover_image_url: (row.cover_image_url as string) ?? null,
    cover_image_alt: (row.cover_image_alt as string) ?? null,
    status: (row.status as ContentItem["status"]) ?? "draft",
    published_at: (row.published_at as string) ?? null,
    tags: arr(row.tags) as string[],
    featured: Boolean(row.featured),
    sort_order: Number(row.sort_order ?? 0),
    cta_label: (row.cta_label as string) ?? null,
    cta_url: (row.cta_url as string) ?? null,
    related_ids: arr(row.related_ids) as string[],
    seo_title: (row.seo_title as string) ?? null,
    seo_description: (row.seo_description as string) ?? null,
    event_starts_at: (row.event_starts_at as string) ?? null,
    event_ends_at: (row.event_ends_at as string) ?? null,
    event_timezone: (row.event_timezone as string) ?? null,
    location: (row.location as string) ?? null,
    is_virtual: Boolean(row.is_virtual),
    registration_url: (row.registration_url as string) ?? null,
    show_inline_form: Boolean(row.show_inline_form),
    recording_url: (row.recording_url as string) ?? null,
    video_provider: (row.video_provider as string) ?? null,
    video_id: (row.video_id as string) ?? null,
    agenda: arr(row.agenda) as AgendaItem[],
    speakers: arr(row.speakers) as Speaker[],
    gated: Boolean(row.gated),
    download_url: (row.download_url as string) ?? null,
    gated_form_source: (row.gated_form_source as string) ?? null,
    hubspot_form_id: (row.hubspot_form_id as string) ?? null,
    evidence_type: (row.evidence_type as string) ?? null,
    authors: (row.authors as string) ?? null,
    journal: (row.journal as string) ?? null,
    year: row.year == null ? null : Number(row.year),
    external_url: (row.external_url as string) ?? null,
    abstract: (row.abstract as string) ?? null,

    sources: (arr(row.sources) as { title?: unknown; url?: unknown }[])
      .map((s) => ({
        title: typeof s?.title === "string" ? s.title : "",
        url: typeof s?.url === "string" ? s.url : "",
      }))
      .filter((s) => s.title),
    author_name: (row.author_name as string) ?? null,
    author_avatar_url: (row.author_avatar_url as string) ?? null,
    read_time_minutes:
      row.read_time_minutes == null ? null : Number(row.read_time_minutes),
    legacy_source_url: (row.legacy_source_url as string) ?? null,
    inline_promos: (Array.isArray(row.inline_promos) ? (row.inline_promos as unknown[]) : [])
      .map((p) => {
        if (!p || typeof p !== "object") return null;
        const r = p as Record<string, unknown>;
        return {
          title: typeof r.title === "string" ? r.title : "",
          text: typeof r.text === "string" ? r.text : "",
          cta_label: typeof r.cta_label === "string" ? r.cta_label : "",
          cta_url: typeof r.cta_url === "string" ? r.cta_url : "",
          image_url: typeof r.image_url === "string" ? r.image_url : "",
          source_insight_id: typeof r.source_insight_id === "string" ? r.source_insight_id : null,
          category: typeof r.category === "string" ? r.category : null,
        } as InlinePromo;
      }),
    blocks: (row.blocks as unknown) ?? null,
    listed: Boolean(row.listed),
    toc_enabled: Boolean(row.toc_enabled),
    toc_levels: Array.isArray(row.toc_levels) ? (row.toc_levels as unknown[]).map(Number) : [2, 3],
  };

}
